/**
 * Cache tags shared by every action that imports an external calculation into a service order
 * (Hyundai AS1Q retrieve, Audatex, Silverdat/MyClaim). Such an import creates jobs and writes items
 * and prices, so the order, its jobs, both total amounts, the checkout and its restrictions all go
 * stale.
 *
 * `serviceOrderJobTotalAmount` is scoped by the order rather than left bare because
 * `getOrderJobItemsTotalAmount` registers it under both the job id and the order id.
 *
 * The `as const` keeps the tag names checked against the tag union — without it they widen to
 * `string` and a typo becomes a silent no-op. This is also why the callers do NOT wrap this in
 * `invalidateOnSuccess`, despite the guidance in libs/dms/api/AGENTS.md: that helper is typed `any`,
 * which erases the check. The cost is that a failed import also invalidates, refetching data that
 * did not change.
 */
export const getServiceOrderImportInvalidationTags = ({
  serviceCaseId,
  serviceOrderId,
}: {
  serviceCaseId: string;
  serviceOrderId: string;
}) =>
  [
    {type: 'orderIntegration', id: serviceOrderId},
    {type: 'serviceOrder', id: serviceOrderId},
    {type: 'serviceOrders', id: serviceCaseId},
    {type: 'serviceOrderJobs', id: serviceOrderId},
    {type: 'serviceOrderTotalAmount', id: serviceOrderId},
    {type: 'serviceOrderJobTotalAmount', id: serviceOrderId},
    {type: 'serviceCase', id: serviceCaseId},
    {type: 'serviceCaseTotalAmount', id: serviceCaseId},
    {type: 'orderProfitability', id: serviceOrderId},
    {type: 'serviceOrderCheckoutRestriction', id: serviceOrderId},
    'afsPaymentIssueRestriction',
    'afsCheckout',
    'afsPaymentsList',
    'afsPaymentByIdList',
  ] as const;
